(ns shared.core.components.table
  (:require [shared.core.components.a :as a]
            [shared.core.components.dropdown :as dd]
            [shared.core.components.icon :as icon]
            [shared.core.components.interactive :refer [interactive]]
            [shared.core.components.input :refer [input]]
            [shared.core.loading :refer [spinner]]
            [shared.core.style :as style]
            [shared.core.reactive :as re]
            [shared.core.error :as error]
            [shared.core.components.theme :as theme]
            [shared.core.util :as util]
            [shared.core.time :as time]
            [clojure.string :as s]))


(defn column-filter-dropdown
  [{:keys [column-index label column-items disable-readable-filter-options?] :as args}]
  [dd/dropdown (merge {:sort-by-text? true
                       :label label
                       :placeholder label
                       :clearable? true
                       :searchable? true
                       :form-name column-index
                       :container-style {:width "100%"}
                       :options (->> column-items
                                     (distinct)
                                     (mapv #(do {:value %
                                                 :text (cond-> %
                                                               (not disable-readable-filter-options?)
                                                               (util/->capitalize-words))})))}
                      args
                      (when disable-readable-filter-options?
                        {:style {:text-transform :none}}))])


(defn table-header-entry [{:keys [label style first? last? first-style disable-readable-filter-options?
                                  filter-column? filter-path column-index column-items expandable? expanded?
                                  on-toggle-expand expanded-content column-id center-content? expandable-icon-style]}]

  (into [theme/theme
         :div (cond-> {:style (merge {:background-color (style/color :white :base)
                                      :color (style/color :gray :shade-4)
                                      :font-size 11
                                      :font-weight style/font-bold-weight
                                      :letter-spacing "1px"
                                      :text-transform :uppercase
                                      :flex 1
                                      :display :flex
                                      :align-items :center
                                      :padding-right (if last? 4 8)
                                      :padding-left (if first? 4 8)}
                                     (when first? first-style)
                                     style)
                       :theme (style/dark-style {:background-color (style/dark-color :surface :base)
                                                 :color (style/dark-color :on-surface :base)})
                       :data-column-index column-index}
                      column-id
                      (assoc :data-column-id column-id))
         (into [:div {:style (cond-> {:color :inherit
                                      :flex "1 0 auto"
                                      :display :flex
                                      :align-items :center}
                                     center-content?
                                     (assoc :justify-content :center))}]
               [(if filter-column?
                  [column-filter-dropdown {:column-index column-index
                                           :label label
                                           :form-path filter-path
                                           :column-items column-items
                                           :disable-readable-filter-options? disable-readable-filter-options?}]
                  label)
                (when expanded-content
                  (into [:div {:style (cond-> {:flex "0 0 auto"
                                               :opacity (if expanded? 1 0)
                                               :transition "opacity 0.2s"}
                                              (not expanded?)
                                              (assoc :display :none))}]
                        [expanded-content]))])
         (when (and expandable? on-toggle-expand)
           [:div {:style {:flex "0 0 auto"
                          :cursor :pointer
                          :display :flex
                          :align-items :center
                          :padding 2
                          :margin-left 2}
                  :on-click on-toggle-expand}
            [icon/material-icon {:icon-type (if expanded? :chevron-left :chevron-right)
                                 :style (merge {:color (util/hex->rgba (style/color :black :base) 0.4)
                                                :font-size 10}
                                               expandable-icon-style)}]])]))

(defn yes-no-boolean [boolean-value]
  (if boolean-value
    [theme/theme
     :span {:style {:color (style/color :green :base)}
            :theme (style/dark-style {:color (style/dark-color :primary :base)})}
     "Yes"]
    [theme/theme
     :span {:style {:color (style/color :red :base)}
            :theme (style/dark-style {:color (style/dark-color :secondary :base)})}
     "No"]))

(defn table-row-entry [_]
  (let [hover?* (re/ratom false)]
    (fn [{:keys [key label style first? last? copyable? copy-content first-style ellipsis-overflow? boolean-value yes-no?
                 expanded? expanded-content actions actions-use-up-style?]}]
      (let [label (if (integer? label) (str label) label)]
        (into [theme/theme
               interactive
               :div {:key key
                     :hover?* hover?*
                     :style (cond-> (merge {:color (style/color :gray :base)
                                            :font-size 14
                                            :flex 1
                                            :display :flex
                                            :align-items :center
                                            :padding-right (if last? 4 8)
                                            :padding-left (if first? 4 8)}
                                           (when ellipsis-overflow?
                                             {:display :block
                                              :line-height 1.3
                                              :overflow :hidden
                                              :text-overflow :ellipsis})
                                           (when first? first-style)
                                           style)
                                    copyable? (assoc :position :relative))
                     :theme (style/dark-style {:color (style/dark-color :on-surface :base)})}
               (when (and copyable? @hover?* (not-empty label))
                 [theme/theme
                  :div {:on-click #(do
                                     (re/dispatch-sync [:core/copy-to-clipboard
                                                        (or copy-content
                                                            (->> (util/find-all-of-type label string?)
                                                                 (s/join " ")))
                                                        {:notify? true}])
                                     (util/stop-event %))
                        :style (cond-> {:background-color (style/color :white :base)
                                        :border-radius 2
                                        :cursor :pointer
                                        :display :flex
                                        :align-items :center
                                        :justify-content :center
                                        :padding 3
                                        :position :absolute
                                        :top "50%"
                                        :right 0
                                        :transform "translateY(-50%)"
                                        :z-index (style/z :low)}
                                       actions-use-up-style?
                                       (assoc :background-color :transparent
                                              :border-radius 3))
                        :theme (style/dark-style {:background-color (style/dark-color :surface :base)
                                                  :color (style/dark-color :on-surface :base)})}
                  [icon/material-icon {:icon-type :content-copy
                                       :style {:color (if actions-use-up-style?
                                                        (style/alc-color "Icons/primary")
                                                        :inherit)
                                               :font-size 14}}]])]
              (concat (cond
                        yes-no? [(yes-no-boolean boolean-value)]
                        label [label]
                        :else "-")
                      (when expanded-content
                        [(into [:div {:style (cond-> {:flex "0 1 auto"
                                                      :opacity (if expanded? 1 0)
                                                      :transition "opacity 0.2s"}
                                                     (not expanded?)
                                                     (assoc :display :none))}]
                               [expanded-content])])))))))

(defn row-action [{:keys [icon-type on-click tooltip-content disabled? style round-style? up-style?]}]
  [theme/theme
   interactive
   :div
   {:style (cond-> {:color (style/color :gray :base)
                    :cursor :pointer
                    :display :flex
                    :align-items :center
                    :justify-content :center
                    :padding 4
                    :transition "0.2s"}
                   disabled? (assoc :cursor :default
                                    :opacity 0.7)
                   up-style? (assoc :color (style/alc-color "Icons/primary"))
                   round-style? (assoc :background-color (style/color :white :base)
                                       :border-radius "50%"
                                       :padding 0)
                   style (merge style))
    :theme (style/dark-style {:color (style/dark-color :on-surface :base)})
    :hover-style (when-not disabled?
                   (merge {:opacity 0.8
                           :cursor :pointer}
                          (when up-style?
                            {:opacity 1})
                          (when round-style?
                            {:background-color (util/hex->rgba (style/color :primary :base) 0.2)
                             :opacity 1})))
    :on-click (when on-click
                (if disabled?
                  util/stop-event
                  #(do
                     (on-click %)
                     (util/stop-event %))))}
   (if tooltip-content
     [icon/material-icon-tooltip {:icon-type icon-type
                                  :tooltip-content tooltip-content
                                  :icon-style {:color (if up-style? (style/alc-color "Icons/primary") :inherit)
                                               :font-size 20}
                                  :style (merge {:color (if up-style? (style/alc-color "Icons/primary") :inherit)
                                                 :height 20}
                                                (when round-style?
                                                  {:display :flex
                                                   :align-items :center
                                                   :justify-content :center
                                                   :height "100%"
                                                   :width "100%"}))}]
     [icon/material-icon {:icon-type icon-type
                          :style {:color (if up-style? (style/alc-color "Icons/primary") :inherit)
                                  :font-size 20}}])])

(defn row-actions [{:keys [actions style x-scroll-position use-sticky-style? action-width round-style?
                           up-style?]
                    :or {action-width 30}}]
  (into [theme/theme
         :div {:style (merge {:background-color (style/color :white :base)
                              :display :flex
                              :align-items :center
                              :justify-content :center
                              :position :absolute
                              :top "50%"
                              :right (- 7 x-scroll-position)
                              :transform "translateY(-50%)"
                              :z-index (style/z :low)}
                             (when round-style?
                               {:border (str "2px solid " (util/hex->rgba (style/color :primary :base) 0.8))
                                :border-radius (/ (+ action-width 4) 2)
                                :right (- 10 x-scroll-position)
                                :height (+ action-width 4)
                                :width (+ (* (count actions) action-width) 4)})
                             (when use-sticky-style?
                               {:position :sticky
                                :top nil
                                :right 7
                                :transform nil
                                :width (* (count actions) action-width)
                                :flex (str "0 0 " (* (count actions) action-width) "px")})
                             (when up-style?
                               {:background-color :transparent
                                :border-radius 3})
                             style)
               :theme (style/dark-style {:background-color (style/dark-color :surface :base)})}]
        (map (fn [a]
               (if (map? a) [row-action (assoc a :style (merge (when (or use-sticky-style? round-style?)
                                                                 {:flex (str "0 0 " action-width "px")})
                                                               (when round-style?
                                                                 {:height action-width})
                                                               (:style a))
                                                 :up-style? up-style?
                                                 :round-style? round-style?)]
                            a))
             actions)))

(defn table-row [_]
  (let [hover?* (re/ratom false)]
    (fn [{:keys [key cells always-show-actions? height actions actions-style style on-click flex-vals x-scroll-position
                 actions-use-sticky-style? round-actions-style? first-style ellipsis-overflow? hover-style
                 expanded-column-indexes actions-use-up-style?]}]
      (let [cells (map #(if (map? %) % {:label %}) cells)]
        (into [theme/theme
               interactive
               :div {:key key
                     :style (merge {:background-color (style/color :white :base)
                                    :border-bottom (str "1px solid " (style/color :gray :shade-7))
                                    :display :flex
                                    :align-items :center
                                    :padding "8px 0"
                                    :transition "0.2s"}
                                   (when height
                                     {:height height})
                                   (when (and actions (not actions-use-sticky-style?))
                                     {:position :relative})
                                   style)
                     :hover-style (when on-click
                                    (merge {:background-color (style/color :gray :shade-9)
                                            :cursor :pointer}
                                           hover-style))
                     :theme (style/dark-prop {:style {:background-color (style/dark-color :surface :base)
                                                      :border-bottom (str "1px solid " (style/dark-color :surface :lighter-1))}
                                              :hover-style (when on-click
                                                             {:background-color (style/dark-color :surface :lighter-1)})})
                     :hover?* hover?*
                     :on-click on-click}]
              (concat (map-indexed (fn [idx r]
                                     [table-row-entry (assoc r :style (merge {:flex 1
                                                                              :padding-top 0
                                                                              :padding-bottom 0}
                                                                             (when flex-vals
                                                                               {:flex (nth flex-vals idx)})
                                                                             (:style r))
                                                               :expanded? (contains? expanded-column-indexes idx)
                                                               :first? (= idx 0)
                                                               :first-style first-style
                                                               :last? (= idx (dec (count cells)))
                                                               :ellipsis-overflow? ellipsis-overflow?
                                                               :actions-use-up-style? actions-use-up-style?)])
                                   cells)
                      (when (and (or always-show-actions? @hover?*) actions)
                        [[row-actions {:actions actions
                                       :round-style? round-actions-style?
                                       :style actions-style
                                       :use-sticky-style? actions-use-sticky-style?
                                       :up-style? actions-use-up-style?
                                       :x-scroll-position x-scroll-position}]])))))))

(defn blank-table-row [{:keys [height]}]
  [theme/theme
   :div {:style {:background-color (style/color :white :base)
                 :border-bottom (str "1px solid " (style/color :gray :shade-7))
                 :height height}
         :theme (style/dark-style {:background-color (style/dark-color :surface :base)
                                   :border-bottom (str "1px solid " (style/dark-color :surface :lighter-1))})}])

(defn no-data [{:keys [container-style style text material-icon phosphor-icon icon-style]}]
  [:div {:style (merge {:color (style/color :on-background :base)
                        :display :flex
                        :flex-direction :column
                        :align-items :center
                        :justify-content :center
                        :padding "40px 25px"
                        :width "100%"}
                       container-style)}
   (when phosphor-icon
     [icon/phosphor-icon {:icon-type phosphor-icon
                          :style (merge {:color :inherit
                                         :font-size 42
                                         :margin-bottom 9}
                                        icon-style)}])
   (when material-icon
     [icon/material-icon {:icon-type material-icon
                          :style (merge {:color :inherit
                                         :font-size 42
                                         :margin-bottom 9}
                                        icon-style)}])
   [theme/theme
    :div {:style (merge {:color (style/color :on-background :base)
                         :font-size 16
                         :font-weight style/font-medium-weight
                         :opacity 0.5}
                        style)
          :theme (style/dark-style {:color (style/dark-color :on-background :base)})}
    (or text "No Data")]])

(defn errors-container [{:keys [container-style errors]}]
  [:div {:style (merge {:display :flex
                        :align-items :center
                        :justify-content :center
                        :padding "40px 25px"
                        :width "100%"}
                       container-style)}
   [error/error-messages {:errors errors}]])

(defn large-no-data [{:keys [style] :as args}]
  [no-data (assoc args :style (merge {:font-size 18
                                      :font-weight style/font-bold-weight}
                                     style))])
(defn cell->label [cell]
  (let [cell-label (or (:raw cell)
                       (if (map? cell)
                         (:label cell)
                         cell))]
    (if (vector? cell-label)
      (last cell-label)
      cell-label)))

(defn apply-column-filters
  [column-idx->filter-value rows]
  (cond->> rows
           (seq column-idx->filter-value)
           (filterv (fn [row]
                      (->> (:cells row)
                           (map-indexed vector)
                           (every? (fn [[idx cell]]
                                     (let [filter-value (column-idx->filter-value idx)]
                                       (or (not filter-value)
                                           (s/includes? (cell->label cell) filter-value))))))))))

(defn table [{:keys [x-scroll-width* row-container-id expanded-form-path]}]
  (let [container-id (gensym "table-")
        x-scroll-container-id (str container-id "-x-scroll")
        row-container-id (or row-container-id (gensym "table-row-container-"))
        search* (re/ratom nil)
        vh- (re/subscribe [:core/viewport-height])
        vw- (re/subscribe [:core/viewport-width])
        dark?- (re/subscribe [:core/theme-dark?-])
        self-scroll-position* (re/ratom 0)
        row-container-scroll-position* (re/ratom 0)
        x-scroll-container-scroll-position* (re/ratom 0)
        x-scroll-width* (or x-scroll-width* (re/ratom nil))
        x-scroll-client-width* (re/ratom nil)
        scroll-position- (re/subscribe [:core/layout-content-container-scroll-position-])
        row-container-off-top* (re/ratom 0)
        visible-row-container-height* (re/ratom 0)
        on-buffers-called* (re/ratom #{})
        show-actions?* (re/ratom true)
        expanded-column-indexes-form-name :expanded-column-indexes
        expanded-column-indexes- (when expanded-form-path
                                   (re/subscribe [:form/val- expanded-form-path expanded-column-indexes-form-name]))
        local-expanded-column-indexes* (re/ratom #{})
        set-virtual-scroll-info #(let [{:keys [virtual-scroll-container-id self-virtual-scroll? on-bottom-buffer
                                               bottom-row-buffer bottom-px-buffer row-height loading?
                                               row-container-scroll? scroll-container-id rows loading-more?
                                               has-more-rows?]
                                        :or {bottom-row-buffer 20}} (re/props %)
                                       virtual-scroll-container-id (cond
                                                                     self-virtual-scroll?
                                                                     container-id

                                                                     row-container-scroll?
                                                                     row-container-id

                                                                     :else
                                                                     virtual-scroll-container-id)
                                       rc (.getElementById js/document row-container-id)
                                       vc (when virtual-scroll-container-id
                                            (.getElementById js/document virtual-scroll-container-id))
                                       sc (js/document.getElementById x-scroll-container-id)]
                                   (reset! x-scroll-width* (.-scrollWidth sc))
                                   (reset! x-scroll-client-width* (.-clientWidth sc))
                                   (if (and rc vc)
                                     (let [rc-rect (.getBoundingClientRect rc)
                                           vc-rect (.getBoundingClientRect vc)
                                           rc-off-top (if row-container-scroll?
                                                        @row-container-scroll-position*
                                                        (- (.-top vc-rect) (.-top rc-rect)))
                                           visible-rc-height (max 0 (min (- (min @vh- (.-bottom rc-rect))
                                                                            (.-top rc-rect))
                                                                         (.-height vc-rect)))]
                                       (reset! row-container-off-top* rc-off-top)
                                       (reset! visible-row-container-height* visible-rc-height)
                                       (when loading? (reset! on-buffers-called* #{}))
                                       (when (and on-bottom-buffer row-height (not loading?))
                                         (let [rows-to-bottom (js/Math.ceil (/ (+ (if (pos? @row-container-off-top*)
                                                                                    @row-container-off-top*
                                                                                    0)
                                                                                  visible-rc-height)
                                                                               row-height))
                                               row-container-height (if row-container-scroll?
                                                                      (.-scrollHeight rc)
                                                                      (.-height rc-rect))
                                               total-row-count (js/Math.round (/ row-container-height row-height))
                                               bottom-record-index (- total-row-count rows-to-bottom)]
                                           (when (and on-bottom-buffer
                                                      (> total-row-count bottom-row-buffer)
                                                      (>= bottom-row-buffer bottom-record-index)
                                                      (not (contains? @on-buffers-called* total-row-count)))
                                             (swap! on-buffers-called* conj total-row-count)
                                             (on-bottom-buffer)))))
                                     ;; This is for when you want to have infinite scroll without using virtual scroll
                                     (when (and has-more-rows?
                                                scroll-container-id
                                                bottom-px-buffer
                                                (not loading?)
                                                (not loading-more?))
                                       (let [total-row-count (count (util/when-not-loading rows))
                                             sc (js/document.getElementById scroll-container-id)]
                                         (when sc
                                           (let [off-bottom (- (.-scrollHeight sc) (.-scrollTop sc) (.-clientHeight sc))]
                                             (when (and on-bottom-buffer
                                                        (<= off-bottom bottom-px-buffer)
                                                        (not (contains? @on-buffers-called* total-row-count)))
                                               (swap! on-buffers-called* conj total-row-count)
                                               (on-bottom-buffer))))))))]
    (re/create-class
      {:component-did-mount
       (fn [this]
         (when (-> (re/props this) :self-virtual-scroll?)
           (set! (.-onscroll (re/dom-node this))
                 #(reset! self-scroll-position* (.. % -target -scrollTop))))
         (set! (.-onscroll (js/document.getElementById x-scroll-container-id))
               #(reset! x-scroll-container-scroll-position* (.. % -target -scrollLeft)))
         (reset! x-scroll-width* (.-scrollWidth (js/document.getElementById x-scroll-container-id)))
         (reset! x-scroll-client-width* (.-clientWidth (js/document.getElementById x-scroll-container-id)))
         (when (-> (re/props this) :row-container-scroll?)
           (set! (.-onscroll (js/document.getElementById row-container-id))
                 #(reset! row-container-scroll-position* (.. % -target -scrollTop))))
         (set-virtual-scroll-info this))
       :component-did-update
       set-virtual-scroll-info
       :reagent-render
       (fn [{:keys [flex-vals style headers searchable? search-placeholder loading? row-container-style rows
                    no-data-text row-height virtual-scroll-container-id self-virtual-scroll? errors loading-more?
                    row-container-scroll? header-style show-actions-toggle? size use-scroll-container-width?
                    loader-content-height x-scroll? loading-more-spinner-args round-actions-style? header-first-style
                    row-first-style row-style ellipsis-overflow? sticky-headers? filter-path
                    disable-readable-filter-options? scroll-container-id loader-container-style loader-cell-style
                    no-data-style no-data-text-style no-data-material-icon no-data-phosphor-icon loading-more-content
                    no-data-icon-style scroll-container-style expandable-icon-style]
             :or {x-scroll? true
                  filter-path [:components :table :filters :by-column-index]}}]
         (let [expandable-columns-config-by-idx (some->> headers
                                                         (map-indexed (fn [idx h]
                                                                        (when (and (map? h) (:expandable? h))
                                                                          [idx h])))
                                                         (filter identity)
                                                         (into {}))
               expanded-column-indexes (or (when expanded-column-indexes- (or @expanded-column-indexes- #{}))
                                           @local-expanded-column-indexes*)
               flex-vals (some->> flex-vals
                                  (map-indexed (fn [idx flex-val]
                                                 (let [expanded-config (get expandable-columns-config-by-idx idx)]
                                                   (if (and (:expanded-flex-val expanded-config)
                                                            (contains? expanded-column-indexes idx))
                                                     (:expanded-flex-val expanded-config)
                                                     flex-val))))
                                  (vec))
               row-border-style (str "1px solid " (cond
                                                    @dark?- (style/dark-color :surface :lighter-1)
                                                    :else (style/color :gray :shade-7)))
               headers (some->> headers (map #(if (map? %) % {:label %})))
               virtual-scroll-container-id (cond
                                             self-virtual-scroll? container-id
                                             row-container-scroll? row-container-id
                                             :else virtual-scroll-container-id)
               x-scroll-container-scroll-position @x-scroll-container-scroll-position*
               x-scroll-width @x-scroll-width*
               x-scroll-client-width @x-scroll-client-width*
               column-idx->filter-value (->> @(re/subscribe [:form/form- filter-path])
                                             (into {} (remove #(= "" (val %)))))]
           @vw-
           (when (or virtual-scroll-container-id scroll-container-id)
             @vh- @scroll-position- @self-scroll-position* @row-container-scroll-position*)
           [:div {:id container-id
                  :style (merge {:display :flex
                                 :flex-direction :column
                                 :position :relative}
                                style)}
            (when show-actions-toggle?
              [:div {:style {:flex "0 0 auto"
                             :display :flex
                             :justify-content :flex-end
                             :margin-bottom 5
                             :width "100%"}}
               [a/a {:on-click #(reset! show-actions?* (not @show-actions?*))
                     :style (condp = size
                              :small {:font-size 13}
                              {})}
                "Toggle Actions"]])
            (when searchable?
              [input {:placeholder (or search-placeholder "Search")
                      :container-style {:margin-bottom 5}
                      :size size
                      :on-change #(reset! search* %)}])
            [:div {:id x-scroll-container-id
                   :style (cond-> {:flex "0 0 auto"
                                   :display :flex
                                   :flex-direction :column
                                   :max-width "100%"}
                                  (some :filter-column? headers) (assoc :min-height 250)
                                  (and x-scroll? (not sticky-headers?)) (assoc :overflow-x :auto)
                                  scroll-container-style (merge scroll-container-style))}
             (when headers
               (into [theme/theme
                      :div {:style (merge {:background-color (style/color :white :base)
                                           :border-bottom row-border-style
                                           :flex "0 0 auto"
                                           :display :flex
                                           :padding "8px 0"}
                                          (when use-scroll-container-width?
                                            {:width x-scroll-width})
                                          (when sticky-headers?
                                            {:position :sticky
                                             :top 0
                                             :left 0
                                             :z-index (dec (style/z :medium))})
                                          header-style)
                            :theme (style/dark-style {:background-color (style/dark-color :surface :base)})}]
                     (map-indexed (fn [idx h]
                                    [table-header-entry
                                     (merge h
                                            {:filter-path filter-path
                                             :column-index idx
                                             :style (merge (when (= (count headers) (count flex-vals))
                                                             {:flex (nth flex-vals idx)})
                                                           (:style h))
                                             :expandable-icon-style expandable-icon-style
                                             :first? (= idx 0)
                                             :first-style header-first-style
                                             :last? (= idx (dec (count headers)))
                                             :expanded? (contains? expanded-column-indexes idx)
                                             :on-toggle-expand #(let [cur-expanded? (contains? expanded-column-indexes idx)
                                                                      op-fn (if cur-expanded? disj conj)]
                                                                  (if expanded-form-path
                                                                    (re/dispatch [:form/change
                                                                                  expanded-form-path
                                                                                  expanded-column-indexes-form-name
                                                                                  (op-fn expanded-column-indexes idx)])
                                                                    (swap! local-expanded-column-indexes* op-fn idx)))}
                                            (when (:filter-column? h)
                                              {:disable-readable-filter-options? disable-readable-filter-options?
                                               :column-items (mapv #(-> %
                                                                        :cells
                                                                        (nth idx)
                                                                        cell->label)
                                                                   rows)}))])
                                  headers)))
             (let [filtered-rows (when-not loading?
                                   (->> rows
                                        (filter (fn [row]
                                                  (if (s/blank? @search*)
                                                    true
                                                    (some #(let [cell-label (cell->label %)
                                                                 exact-tags (:exact-tags %)
                                                                 lc-search (s/lower-case @search*)]
                                                             (or (s/includes? (s/lower-case (str cell-label))
                                                                              lc-search)
                                                                 (some->> exact-tags
                                                                          (filter identity)
                                                                          (some (fn [tag] (= (str tag) lc-search))))))
                                                          (:cells row)))))
                                        ((partial apply-column-filters column-idx->filter-value))))
                   virtual-scroll-buffer 10
                   visible-row-count (when (and virtual-scroll-container-id
                                                (pos? row-height)
                                                (pos? @visible-row-container-height*))
                                       (js/Math.ceil (/ @visible-row-container-height* row-height)))
                   first-visible-rows-idx (when (and virtual-scroll-container-id
                                                     (pos? row-height))
                                            (max 0 (js/Math.floor (/ @row-container-off-top* row-height))))
                   drop-count (when (> first-visible-rows-idx virtual-scroll-buffer)
                                (- first-visible-rows-idx virtual-scroll-buffer))
                   take-count (when (pos? visible-row-count)
                                (+ (min virtual-scroll-buffer first-visible-rows-idx)
                                   visible-row-count
                                   virtual-scroll-buffer))]
               (into [:div {:id row-container-id
                            :style (merge (when row-container-scroll?
                                            {:overflow :auto})
                                          (when use-scroll-container-width?
                                            {:width x-scroll-width})
                                          row-container-style)}]
                     (cond
                       loading? (repeat 4
                                        [table-row {:cells (map (fn [_]
                                                                  {:label [:div {:style {:background-color (style/color :gray :shade-4)
                                                                                         :border-radius 2
                                                                                         :opacity 0.1
                                                                                         :height (or loader-content-height
                                                                                                     10)
                                                                                         :width "30%"}}]
                                                                   :style loader-cell-style})
                                                                headers)
                                                    :flex-vals (when (= (count headers)
                                                                        (count flex-vals))
                                                                 flex-vals)
                                                    :style (cond-> loader-container-style
                                                                   row-height (assoc :height row-height))}])

                       errors [[errors-container {:errors errors}]]

                       (some-> filtered-rows (count) (pos?))
                       (let [rows (if virtual-scroll-container-id
                                    (->> (if drop-count (drop drop-count filtered-rows) filtered-rows)
                                         (take (or take-count (* 2 virtual-scroll-buffer))))
                                    filtered-rows)
                             remaining-count (when (pos? visible-row-count)
                                               (- (count filtered-rows) drop-count take-count))]
                         (concat (when (pos? drop-count)
                                   (repeat drop-count [blank-table-row {:height row-height}]))
                                 (map (fn [r]
                                        (let [cells (if (vector? r)
                                                      r
                                                      (:cells r))]
                                          [table-row {:key (:key r)
                                                      :expanded-column-indexes expanded-column-indexes
                                                      :cells cells
                                                      :actions (:actions r)
                                                      :actions-style (:actions-style r)
                                                      :actions-use-sticky-style? (:actions-use-sticky-style? r)
                                                      :actions-use-up-style? (:actions-use-up-style? r)
                                                      :always-show-actions? (and @show-actions?*
                                                                                 (:always-show-actions? r))
                                                      :round-actions-style? round-actions-style?
                                                      :style (merge row-style (:style r))
                                                      :first-style row-first-style
                                                      :on-click (:on-click r)
                                                      :hover-contents (:hover-contents r)
                                                      :hover-style (:hover-style r)
                                                      :height row-height
                                                      :x-scroll-position x-scroll-container-scroll-position
                                                      :flex-vals (when (= (count cells)
                                                                          (count flex-vals))
                                                                   flex-vals)
                                                      :ellipsis-overflow? ellipsis-overflow?}]))
                                      rows)
                                 (when (and (pos? remaining-count))
                                   (repeat remaining-count [blank-table-row {:height row-height}]))))

                       :else
                       [[no-data {:text no-data-text
                                  :style no-data-text-style
                                  :icon-style no-data-icon-style
                                  :container-style no-data-style
                                  :material-icon no-data-material-icon
                                  :phosphor-icon no-data-phosphor-icon}]])))
             (when loading-more?
               (or loading-more-content
                   [:div {:style {:display :flex
                                  :align-items :center
                                  :justify-content :center
                                  :padding 10
                                  :width "100%"}}
                    [spinner (merge {:color (style/color :green :base)}
                                    loading-more-spinner-args)]]))]]))})))

(defn primary-table [{:keys [style row-first-style row-style] :as args}]
  [table (assoc args :style (merge {:border (str "1px solid " (style/color :gray :shade-7))
                                    :border-radius "8px"
                                    :overflow :hidden}
                                   style)
                     :header-first-style {:padding-left 14}
                     :row-first-style (merge {:padding-left 14}
                                             row-first-style)
                     :row-style (merge {:padding "15px 0"}
                                       row-style)
                     :round-actions-style? true
                     :loading-more-spinner-args {:color (style/color :primary :base)})])

(defn db-rows-table [{:keys [db-data row-key table-args header-sort-fn date-suffix convert-dates-to-local-time?
                             date-format collection-separator disable-readable-number? sticky-headers? searchable?
                             filter-columns? disable-readable-filter-options?]
                      :or {row-key :row
                           date-format "M-d-yyyy h:mm:ss A"
                           collection-separator ", "}}]
  (let [loading? (util/loading? db-data)
        header-keys (when-not loading?
                      (-> db-data
                          (first)
                          (get row-key)
                          (keys)
                          (#(if header-sort-fn
                              (sort header-sort-fn %)
                              %))))]
    [table (merge {:loading? loading?
                   :sticky-headers? sticky-headers?
                   :searchable? searchable?
                   :no-data-text "No Data"
                   :disable-readable-filter-options? disable-readable-filter-options?
                   :headers (when-not loading?
                              (mapv #(merge {:label (util/->capitalize-words %)}
                                            (when filter-columns?
                                              {:filter-column? true}))
                                    header-keys))}
                  (when-not loading?
                    {:rows (mapv (fn [r]
                                   (let [r-data (get r row-key)
                                         row-error-fields (get r :row-error-fields)
                                         on-click (get r :on-click)]
                                     {:cells (mapv (fn [k]
                                                     (let [v (get r-data k)
                                                           coll? (coll? v)
                                                           date? (not (nil? (time/try-from-string v)))
                                                           bool? (boolean? v)
                                                           int? (integer? v)
                                                           ipv6? (util/ipv6? v)
                                                           cell-v (cond
                                                                    coll? (if (empty? v) nil (s/join collection-separator v))
                                                                    int? (if disable-readable-number? v (util/readable-number v))
                                                                    date? (str (if convert-dates-to-local-time?
                                                                                 (-> v
                                                                                     (time/utc->local)
                                                                                     (time/as-format date-format))
                                                                                 (time/reformat-date-str v date-format))
                                                                               date-suffix)
                                                                    bool? (when v "Yes")
                                                                    :else v)]
                                                       {:label cell-v
                                                        :copyable? (not (nil? v))
                                                        :style (cond-> {}
                                                                       (some-> row-error-fields (get k))
                                                                       (assoc :color (style/color :red :error))

                                                                       ipv6?
                                                                       (assoc :overflow-wrap :anywhere))}))
                                                   header-keys)
                                      :on-click (when on-click #(on-click r-data))
                                      :actions (get r :row-actions)
                                      :actions-style (get r :row-actions-style)
                                      :actions-use-sticky-style? (get r :row-actions-use-sticky-style?)
                                      :actions-use-up-style? (get r :actions-use-up-style?)
                                      :style (get r :row-style)}))
                                 db-data)})
                  table-args)]))
